import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  searchCachedContacts, getContactsCacheStatus, refreshContacts,
  type GoogleContact, type ContactsCacheStatus,
} from './api'

export interface ContactSearch {
  results:   GoogleContact[]
  searching: boolean
  pulling:   boolean
  pullError: string | null
}

/**
 * Search the cached Google contacts. If the cache turns out to be empty, pull
 * it from Google once, in-place — callers never have to send the user to
 * Settings to "sync" first. The query is debounced internally.
 */
export function useContactSearch(rawQuery: string, enabled = true): ContactSearch {
  const queryClient = useQueryClient()
  const [q, setQ]   = useState(rawQuery.trim())

  useEffect(() => {
    const t = setTimeout(() => setQ(rawQuery.trim()), 200)
    return () => clearTimeout(t)
  }, [rawQuery])

  const statusQ = useQuery<ContactsCacheStatus>({
    queryKey: ['contacts-cache-status'],
    queryFn:  getContactsCacheStatus,
    enabled,
  })

  const cacheEmpty = statusQ.isSuccess && statusQ.data.count === 0

  const pull = useMutation({
    mutationFn: refreshContacts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts-cache-status'] })
      queryClient.invalidateQueries({ queryKey: ['contacts-search'] })
    },
  })

  // Auto-pull exactly once when we learn the cache is empty (idle → pending).
  useEffect(() => {
    if (enabled && cacheEmpty && pull.isIdle) pull.mutate()
  }, [enabled, cacheEmpty, pull.isIdle])

  const search = useQuery<GoogleContact[]>({
    queryKey: ['contacts-search', q],
    queryFn:  () => searchCachedContacts(q),
    enabled:  enabled && q.length > 0 && !cacheEmpty,
  })

  return {
    results:   search.data ?? [],
    searching: search.isFetching,
    pulling:   pull.isPending,
    pullError: pull.error ? (pull.error as Error).message : null,
  }
}
